/**
 * Every keystroke the editor answers to, written out for the help sheet.
 *
 * It is a separate table from the mapping in `score-keys.ts` on purpose: one says what a key
 * does, the other says it in words a person reads. They are checked against each other by
 * hand, which is cheap because both are short and both are in this folder.
 */

export interface Shortcut {
  readonly keys: string;
  readonly what: string;
}

export interface ShortcutGroup {
  readonly title: string;
  readonly keys: readonly Shortcut[];
}

export const SHORTCUTS: readonly ShortcutGroup[] = [
  {
    title: "Writing notes",
    keys: [
      { keys: "A–G", what: "write that note where the caret stands" },
      { keys: "shift + A–G", what: "add that note to the chord just written" },
      { keys: "1–6", what: "semibreve through demisemiquaver" },
      { keys: "Q / W", what: "halve or double the value, on any staff" },
      { keys: ".", what: "dot the value, or undot it" },
      { keys: "/", what: "triplet, or back to plain" },
      { keys: "R", what: "write a rest" },
      { keys: "T", what: "tie the next note back into the last" },
      { keys: "S", what: "write grace notes onto the note the caret stands at" },
      { keys: "shift + S", what: "the same, played on the beat rather than before it" },
      { keys: "⌫", what: "clear what is under the caret" },
      {
        keys: "a MIDI keyboard",
        what: "play the note in; hold one key and play another to build the chord",
      },
    ],
  },
  {
    title: "Moving and tuning",
    keys: [
      { keys: "← →", what: "move the caret a note at a time" },
      { keys: "↑ ↓", what: "move the note a semitone" },
      { keys: "alt + ↑ ↓", what: "move it a letter, keeping the accidental" },
      { keys: "shift + ↑ ↓", what: "move it an octave" },
    ],
  },
  {
    title: "On a tab staff",
    keys: [
      { keys: "0–9", what: "the fret, on the string the caret is on" },
      { keys: "1 then 2", what: "two digits make one fret: this is the twelfth" },
      { keys: "alt + 0–9", what: "the frets above nine, without the two-digit run" },
      { keys: "alt + shift + 0–9", what: "the frets above nineteen, on a neck that long" },
      { keys: "↑ ↓", what: "move to the string above or below" },
      { keys: "Q / W", what: "halve or double the value, since the digits are frets" },
      { keys: "shift + 1–6", what: "pick a value outright, the plain digits being frets" },
    ],
  },
  {
    title: "Editing",
    keys: [
      { keys: "⌘Z / ⇧⌘Z", what: "undo, redo" },
      { keys: "⌘X ⌘C ⌘V", what: "cut, copy, paste the selection" },
      { keys: "⌘A", what: "select the whole score" },
      { keys: "⌘K", what: "type a chord symbol over the selection" },
      { keys: "⌘G", what: "type figured bass under the selection" },
      { keys: "click, shift + click", what: "select a note, or a passage up to it" },
      { keys: "⌘ + ← ↑ → ↓", what: "move what is selected a little way on the page" },
      { keys: "V", what: "start another voice on this staff, over the selected bars" },
      { keys: "shift + V", what: "take that voice off the staff again" },
      { keys: "⌘⇧ + ↑ ↓", what: "draw the selection on the staff above or below" },
    ],
  },
  {
    title: "Playing",
    keys: [
      { keys: "space", what: "play from the selection, or pause" },
      { keys: "esc", what: "stop and go back to the start" },
      { keys: "L", what: "repeat the selected passage" },
      { keys: "M", what: "sound a click on every beat" },
    ],
  },
  {
    title: "The file and the page",
    keys: [
      { keys: "⌘O", what: "open a score, a MusicXML file or a MIDI file" },
      { keys: "⌘S", what: "save the document" },
      { keys: "⌘E", what: "export MusicXML" },
      { keys: "⌘P", what: "print, which is also how a PDF is made" },
      { keys: "⌘+ / ⌘−", what: "draw the page larger or smaller" },
      { keys: "?", what: "this sheet" },
    ],
  },
];
